import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Spin,
  Collapse,
  Tooltip,
  Icon,
  Alert,
  Popover,
} from 'antd'
import { ModalBlur } from '../../components'
import { frontends } from './helper/index'
import { formatSize } from '../../utils/formatter'
import { formatDate, safeParseJSON } from '../../utils/formatDate'
import { sortByCreatedTime } from '../../utils/sort'

const FormItem = Form.Item
const { Panel } = Collapse
const { Option } = Select

const formItemLayout = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 13,
  },
}

const formItemLayoutForAdvanced = {
  labelCol: {
    span: 9,
  },
  wrapperCol: {
    span: 14,
  },
}

const dataSourceOptions = ['Volume', 'Volume Snapshot']

const getDataSource = (getFieldValue) => {
  let dataSource = ''
  const dataSourceType = getFieldValue('dataSourceType') || ''
  const dataSourceVol = getFieldValue('dataSourceVolume') || ''
  const dataSourceSnapshot = getFieldValue('dataSourceSnapshot') || ''
  if (dataSourceType && dataSourceVol) {
    switch (dataSourceType) {
      case 'Volume':
        dataSource = `vol://${dataSourceVol}`
        break
      case 'Volume Snapshot':
        dataSource = dataSourceSnapshot ? `snap://${dataSourceVol}/${dataSourceSnapshot}` : ''
        break
      default:
    }
  }
  return dataSource
}

const getSize = (getFieldValue, volumeOptions) => {
  const dataSourceType = getFieldValue('dataSourceType') || ''
  const dataSourceVol = getFieldValue('dataSourceVolume') || ''
  const dataSourceSnapshot = getFieldValue('dataSourceSnapshot') || ''

  if (dataSourceType && dataSourceType === dataSourceOptions[0] && dataSourceVol) {
    const sourceVolSize = volumeOptions.find(vol => vol.name === dataSourceVol)?.size || 0
    return sourceVolSize
  }

  if (dataSourceType && dataSourceType === dataSourceOptions[1] && dataSourceVol && dataSourceSnapshot) {
    const sourceVolSize = volumeOptions.find(vol => vol.name === dataSourceVol)?.size || 0
    return sourceVolSize
  }

  return `${getFieldValue('size')}${getFieldValue('unit')}`
}

const genOkData = (getFieldsValue, getFieldValue, volumeOptions) => {
  const data = {
    ...getFieldsValue(),
    dataSource: getDataSource(getFieldValue),
    size: getSize(getFieldValue, volumeOptions),
    snapshotMaxSize: `${getFieldsValue().snapshotMaxSize}${getFieldsValue().snapshotSizeUnit}`,
  }
  if (data.dataSourceType) {
    delete data.dataSourceType
  }
  if (data.unit) {
    delete data.unit
  }
  return data
}


const modal = ({
  item,
  volumeOptions = [],
  snapshotsOptions = [],
  visible,
  onCancel,
  onOk,
  nodeTags,
  defaultDataLocalityOption,
  defaultDataLocalityValue,
  defaultRevisionCounterValue,
  defaultSnapshotDataIntegrityOption,
  diskTags,
  backingImageOptions,
  tagsLoading,
  backupTargets,
  v1DataEngineEnabled,
  v2DataEngineEnabled,
  getSnapshot,
  form: {
    getFieldDecorator,
    validateFields,
    getFieldsValue,
    getFieldValue,
    setFieldsValue,
    resetFields
  },
}) => {
  const [filteredBackingImages, setFilteredBackingImages] = useState([])

  useEffect(() => {
    const dataEngine = getFieldValue('dataEngine')
    setFilteredBackingImages(backingImageOptions.filter(image => image.dataEngine === dataEngine))
  }, [])
  function handleOk() {
    validateFields((errors) => {
      if (errors) {
        return
      }
      const data = genOkData(getFieldsValue, getFieldValue, volumeOptions)
      onOk(data)
    })
  }

  const modalOpts = {
    title: 'Create Volume',
    visible,
    onCancel,
    width: 880,
    onOk: handleOk,
    style: { top: 0 },
  }

  function unitChange(value) {
    let currentSize = getFieldsValue().size

    if (value === 'Gi') {
      currentSize /= 1024
    } else {
      currentSize *= 1024
    }
    setFieldsValue({
      ...getFieldsValue(),
      unit: value,
      size: currentSize,
    })
  }
  const displayDataSourceAlert = () => {
    const selectedVol = getFieldValue('dataSourceVolume')
    if (selectedVol === '' || selectedVol === undefined) {
      return false
    } else {
      return true
    }
  }

  const handleDataSourceTypeChange = () => {
    setFieldsValue({
      ...getFieldsValue(),
      dataSourceVolume: '',
      dataSourceSnapshot: ''
    })
  }

  const handleDataSourceVolumeChange = (value) => {
    const dataSourceVol = volumeOptions.find(vol => vol.name === value)

    if (getFieldValue('dataSourceType') === dataSourceOptions[1] && dataSourceVol) {
      getSnapshot(dataSourceVol)
    }

    setFieldsValue({
      ...getFieldsValue(),
      size: formatSize(dataSourceVol),
      dataSourceSnapshot: '',
    })
  }

  const dataSourceVolume = getFieldValue('dataSourceVolume')
  const volumeSnapshots = dataSourceVolume && snapshotsOptions?.length > 0
    ? snapshotsOptions.filter(snap => snap.name !== 'volume-head')
    : []
  sortByCreatedTime(volumeSnapshots)
  const dataSourceAlertMsg = 'The volume size is set to the selected volume size. Mismatched size will cause create volume failed.'

  const parsedReplicas = safeParseJSON(item.numberOfReplicas || '{}')
  const parsedUblkNumberOfQueue = safeParseJSON(item.ublkNumberOfQueue || '{}')
  const parsedUblkQueueDepth = safeParseJSON(item.ublkQueueDepth || '{}')

  const initialDataEngine = v1DataEngineEnabled ? 'v1' : 'v2'
  const initialReplicas = parseInt(parsedReplicas[initialDataEngine] ?? 3, 10)

  const currentDataEngine = getFieldValue('dataEngine')
  const initialUblkNumberOfQueue = Number(parsedUblkNumberOfQueue[currentDataEngine] ?? 0)
  const initialUblkQueueDepth = Number(parsedUblkQueueDepth[currentDataEngine] ?? 0)

  // filter options based on selected data engine version
  const handleDataEngineChange = (engine) => {
    const fields = getFieldsValue()
    const replicas = parseInt(parsedReplicas[engine] ?? 3, 10)

    // update filtered backing images
    setFilteredBackingImages(backingImageOptions.filter(image => image.dataEngine === engine))
    setFieldsValue({
      ...fields,
      numberOfReplicas: replicas, // update number of replicas
      backingImage: '',
      frontend: frontends[0].value,
      dataSourceVolume: '',
      dataSourceSnapshot: '',
    })

    if (engine === 'v2') {
      resetFields(['rebuildConcurrentSyncLimit'])
    }
  }

  return (
    <ModalBlur {...modalOpts}>
      <Form layout="horizontal">
        <FormItem label="Name" hasFeedback {...formItemLayout}>
          {getFieldDecorator('name', {
            initialValue: item.name || '',
            rules: [
              {
                required: true,
                message: 'Please input volume name',
              },
            ],
          })(<Input />)}
        </FormItem>
        <div style={{ display: 'flex' }}>
          <FormItem label="Size" style={{ flex: '1 0 65%', paddingLeft: 30 }} labelCol={{ span: 8 }} wrapperCol={{ span: 14 }}>
            {getFieldDecorator('size', {
              initialValue: item.size,
              rules: [
                {
                  required: true,
                  message: 'Please input volume size',
                }, {
                  validator: (rule, value, callback) => {
                    if (value === '' || typeof value !== 'number') {
                      callback()
                      return
                    }
                    if (value < 0 || value > 65536) {
                      callback('The value should be between 0 and 65535')
                    } else if (!/^\d+([.]\d{1,2})?$/.test(value)) {
                      callback('This value should have at most two decimal places')
                    } else if (value < 10 && getFieldsValue().unit === 'Mi') {
                      callback('The volume size must be greater than 10 Mi')
                    } else if (value % 1 !== 0 && getFieldsValue().unit === 'Mi') {
                      callback('Decimals are not allowed')
                    } else {
                      callback()
                    }
                  },
                },
              ],
            })(<InputNumber min={0} max={65535} style={{ width: '330px' }} />)}
          </FormItem>
          <FormItem style={{ flex: '1 0 30%' }}>
            {getFieldDecorator('unit', {
              initialValue: item.unit || 'Gi',
              rules: [{ required: true, message: 'Please select your unit!' }],
            })(
              <Select
                style={{ width: '100px' }}
                onChange={unitChange}
              >
                <Option value="Mi">Mi</Option>
                <Option value="Gi">Gi</Option>
              </Select>,
            )}
          </FormItem>
        </div>
        <FormItem label="Data Engine" hasFeedback {...formItemLayout}>
          {getFieldDecorator('dataEngine', {
            initialValue: initialDataEngine,
            rules: [
              {
                validator: (_rule, value, callback) => {
                  if (value === 'v1' && !v1DataEngineEnabled) {
                    callback('v1 data engine is not enabled')
                  } else if (value === 'v2' && !v2DataEngineEnabled) {
                    callback('v2 data engine is not enabled')
                  }
                  callback()
                },
              },
            ],
          })(<Select onChange={handleDataEngineChange}>
            <Option key={'v1'} value={'v1'}>v1</Option>
            <Option key={'v2'} value={'v2'}>v2</Option>
          </Select>)}
        </FormItem>
        <FormItem label="Number of Replicas" hasFeedback {...formItemLayout}>
          {getFieldDecorator('numberOfReplicas', {
            initialValue: initialReplicas,
            rules: [
              { required: true, message: 'Please input the number of replicas' },
              {
                validator: (_rule, value, callback) => {
                  if (value === '' || typeof value !== 'number') {
                    callback()
                    return
                  }
                  if (value < 1 || value > 10) {
                    callback('The value should be between 1 and 10')
                  } else if (!/^\d+$/.test(value)) {
                    callback('The value must be a positive integer')
                  } else {
                    callback()
                  }
                },
              },
            ],
          })(<InputNumber />)}
        </FormItem>
        <FormItem label="Frontend" hasFeedback {...formItemLayout}>
          {getFieldDecorator('frontend', {
            initialValue: frontends[0].value,
            rules: [{ required: true, message: 'Please select a frontend' }],
          })(
            <Select>
              {frontends.map(({ value, label, dataEngine }) => {
                const selectedDataEngine = getFieldValue('dataEngine')
                return dataEngine.includes(selectedDataEngine) ? (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ) : null
              })}
            </Select>
          )}
        </FormItem>
        {getFieldValue('frontend') === 'ublk' && (
          <>
            {['ublkNumberOfQueue', 'ublkQueueDepth'].map((field) => {
              const label = field === 'ublkNumberOfQueue'
                ? 'UBLK Number of Queue'
                : 'UBLK Queue Depth'

              const initialValue = field === 'ublkNumberOfQueue'
                ? initialUblkNumberOfQueue
                : initialUblkQueueDepth

              const requiredMessage = field === 'ublkNumberOfQueue'
                ? 'Please input the UBLK number of the queue'
                : 'Please input the UBLK queue depth'

              return (
                <FormItem key={field} label={label} hasFeedback {...formItemLayout}>
                  {getFieldDecorator(field, {
                    initialValue,
                    rules: [{ required: true, message: requiredMessage }],
                  })(
                    <InputNumber
                      parser={(value) => Number(String(value).replace(/[^\d]/g, ''))}
                      min={0}
                    />
                  )}
                </FormItem>
              )
            })}
          </>
        )}
        <FormItem label="Data Locality" hasFeedback {...formItemLayout}>
          {getFieldDecorator('dataLocality', {
            initialValue: defaultDataLocalityValue,
          })(<Select>
          { defaultDataLocalityOption.map(value => <Option key={value} value={value}>{value}</Option>) }
          </Select>)}
        </FormItem>
        <FormItem label="Access Mode" hasFeedback {...formItemLayout}>
          {getFieldDecorator('accessMode', {
            initialValue: 'rwo',
          })(<Select>
            <Option key={'ReadWriteOnce'} value={'rwo'}>ReadWriteOnce</Option>
            <Option key={'ReadWriteOncePod'} value={'rwop'}>ReadWriteOncePod</Option>
            <Option key={'ReadWriteMany'} value={'rwx'}>ReadWriteMany</Option>
          </Select>)}
        </FormItem>
        <FormItem label="Backing Image" hasFeedback {...formItemLayout}>
          {getFieldDecorator('backingImage', {
            initialValue: '',
          })(<Select allowClear>
              { filteredBackingImages.map(backingImage => <Option key={backingImage.name} value={backingImage.name}>{backingImage.name}</Option>) }
          </Select>)}
        </FormItem>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <FormItem label={
              <span>
                Data Source
                <span style={{
                  marginLeft: 4,
                  marginRight: 4,
                }}>
                  <Tooltip
                    overlayStyle={{ width: 450 }}
                    title="Choose data source from existing volume or snapshot. Longhorn will clone the volume data from selected data source"
                  >
                    <Icon type="question-circle-o" />
                  </Tooltip>
                </span>
              </span>}
            hasFeedback
            {...formItemLayout}>
            {getFieldDecorator('dataSourceType', { initialValue: '' })(
              <Select allowClear onChange={handleDataSourceTypeChange}>
                {dataSourceOptions.map(value => <Option key={value} value={value}>{value}</Option>) }
              </Select>
            )}
          </FormItem>
          {getFieldValue('dataSourceType') && (<Popover placement="right"
            visible={displayDataSourceAlert()}
            content={
              <div style={{ maxWidth: 300 }}>
                <Alert message={dataSourceAlertMsg} type="warning" />
              </div>
            }
          >
            <FormItem label="Volume" hasFeedback {...formItemLayout}>
              {getFieldDecorator('dataSourceVolume', { initialValue: '' })(
                (() => {
                  const selectedDataEngine = getFieldValue('dataEngine')
                  const filteredOptions = volumeOptions
                    .filter(vol => vol.dataEngine === selectedDataEngine)
                    .map(vol => (
                      <Option key={vol.name} value={vol.name}>
                        {vol.name}
                      </Option>
                    ))

                  return (
                    <Select allowClear onChange={handleDataSourceVolumeChange}>
                      {filteredOptions}
                    </Select>
                  )
                })()
              )}
            </FormItem>
          </Popover>
          )}
        </div>
        {getFieldValue('dataSourceType') === dataSourceOptions[1] && <FormItem label="Snapshot" hasFeedback {...formItemLayout}>
          {getFieldDecorator('dataSourceSnapshot', { initialValue: '' })(
            <Select allowClear optionLabelProp="label" dropdownMenuStyle={{ width: `${volumeSnapshots.length > 0 ? 'fit-content' : 'auto'}` }}>
              {volumeSnapshots.map(snap => <Option key={snap.name} value={snap.name} label={snap.name}>{`${snap.name} (created ${formatDate(snap.created, false)})`}</Option>)}
            </Select>
          )}
          </FormItem>
        }
        <FormItem label="Backup Target" hasFeedback {...formItemLayout}>
          {getFieldDecorator('backupTargetName', {
            // init backup target is the default one
            initialValue: backupTargets.find(bt => bt.name === 'default')?.name || '',
          })(<Select allowClear>
            { backupTargets.map(bt => <Option key={bt.name} disabled={bt.available === false} value={bt.name}>{bt.name}</Option>)}
          </Select>)}
        </FormItem>
        <FormItem label="Encrypted" {...formItemLayout}>
          {getFieldDecorator('encrypted', {
            valuePropName: 'checked',
            initialValue: false,
          })(<Checkbox></Checkbox>)}
        </FormItem>
        <Spin spinning={tagsLoading}>
          <FormItem label="Node Tag" hasFeedback {...formItemLayout}>
            {getFieldDecorator('nodeSelector', {
              initialValue: [],
            })(<Select mode="tags">
            { nodeTags.map(opt => <Option key={opt.id} value={opt.id}>{opt.name}</Option>) }
            </Select>)}
          </FormItem>
        </Spin>
        <Spin spinning={tagsLoading}>
          <FormItem label="Disk Tag" hasFeedback {...formItemLayout}>
            {getFieldDecorator('diskSelector', {
              initialValue: [],
            })(<Select mode="tags">
            { diskTags.map(opt => <Option key={opt.id} value={opt.id}>{opt.name}</Option>) }
            </Select>)}
          </FormItem>
        </Spin>
        {/* Advanced Configurations */}
        <Collapse>
          <Panel header="Advanced Configurations" key="1">
            <FormItem label="Backup Block Size" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('backupBlockSize', { initialValue: '0' })(
                <Select>
                  <Option key={'ignored'} value={'0'}>ignored (follow the global setting)</Option>
                  <Option key={'2Mi'} value={'2097152'}>2 Mi</Option>
                  <Option key={'16Mi'} value={'16777216'}>16 Mi</Option>
                </Select>
              )}
            </FormItem>
            <FormItem label="Snapshot Data Integrity" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('snapshotDataIntegrity', {
                initialValue: 'ignored',
              })(<Select>
              {defaultSnapshotDataIntegrityOption.map(option => <Option key={option.key} value={option.value}>{option.key}</Option>) }
              </Select>)}
            </FormItem>
            <FormItem label={
              <span>
                Snapshot Max Count
                <span style={{
                  marginLeft: 4,
                  marginRight: 4,
                }}>
                  <Tooltip title="Set '0' to inherit global settings">
                    <Icon type="question-circle-o" />
                  </Tooltip>
                </span>
              </span>
            }
              {...formItemLayoutForAdvanced}
            >
              {getFieldDecorator('snapshotMaxCount', {
                initialValue: 0,
              })(<InputNumber style={{ width: '250px' }} />) }
            </FormItem>
              <FormItem
                label={
                <span>
                  Snapshot Max Size
                  <span style={{
                    marginLeft: 4,
                    marginRight: 4,
                  }}>
                    <Tooltip title="Set '0' for unrestricted size or at least twice volume size">
                      <Icon type="question-circle-o" />
                    </Tooltip>
                  </span>
                </span>}
                {...formItemLayoutForAdvanced}
              >
                <div style={{ display: 'flex', gap: 10 }}>
                  {getFieldDecorator('snapshotMaxSize', {
                    initialValue: '0',
                  })(<Input style={{ maxWidth: '250px' }} />)}
                  {getFieldDecorator('snapshotSizeUnit', {
                    initialValue: item.unit || 'Gi',
                    rules: [{ required: true, message: 'Please select your unit!' }],
                  })(
                    <Select
                      style={{ width: '100px' }}
                      onChange={unitChange}
                    >
                      <Option value="Mi">Mi</Option>
                      <Option value="Gi">Gi</Option>
                    </Select>,
                  )}
                </div>
              </FormItem>
              {getFieldValue('dataEngine') === 'v1' && (
                <FormItem
                  label={
                    <span>
                      Rebuild Concurrent Sync Limit
                      <span style={{ marginLeft: 4, marginRight: 4 }}>
                        <Tooltip title="Set '0' to inherit global settings">
                          <Icon type="question-circle-o" />
                        </Tooltip>
                      </span>
                    </span>
                  }
                  {...formItemLayoutForAdvanced}
                >
                  {getFieldDecorator('rebuildConcurrentSyncLimit', {
                    initialValue: 0,
                    rules: [
                      { type: 'number', min: 0, max: 5, message: 'Value must be between 0 and 5' }
                    ],
                  })(
                    <InputNumber min={0} max={5} style={{ width: '250px' }} />
                  )}
                </FormItem>
              )}
            <FormItem label="Replicas Auto Balance" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('replicaAutoBalance', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'least-effort'} value={'least-effort'}>least-effort</Option>
                <Option key={'best-effort'} value={'best-effort'}>best-effort</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Allow Snapshot Removal During Trim" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('unmapMarkSnapChainRemoved', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Replica Soft Anti Affinity" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('replicaSoftAntiAffinity', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Replica Zone Soft Anti Affinity" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('replicaZoneSoftAntiAffinity', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Replica Disk Soft Anti Affinity" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('replicaDiskSoftAntiAffinity', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Freeze Filesystem For Snapshot" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('freezeFilesystemForSnapshot', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            <FormItem label="Offline Replica Rebuilding" hasFeedback {...formItemLayoutForAdvanced}>
              {getFieldDecorator('offlineRebuilding', {
                initialValue: 'ignored',
              })(<Select>
                <Option key={'enabled'} value={'enabled'}>enabled</Option>
                <Option key={'disabled'} value={'disabled'}>disabled</Option>
                <Option key={'ignored'} value={'ignored'}>ignored (follow the global setting)</Option>
              </Select>)}
            </FormItem>
            {getFieldValue('dataEngine') === 'v2' && (
              <FormItem
                label={
                  <span>Replica Rebuilding Bandwidth Limit
                    <span style={{ marginLeft: 4, marginRight: 4 }}>
                      <Tooltip title="Set '0' to inherit global settings">
                        <Icon type="question-circle-o" />
                      </Tooltip>
                    </span>
                  </span>
                }
                {...formItemLayoutForAdvanced}
              >
                {getFieldDecorator('replicaRebuildingBandwidthLimit', {
                  initialValue: 0,
                })(<InputNumber style={{ width: '250px' }} />) }
              </FormItem>
            )}
            <FormItem label="Disable Revision Counter" {...formItemLayoutForAdvanced}>
              {getFieldDecorator('revisionCounterDisabled', {
                valuePropName: 'checked',
                initialValue: defaultRevisionCounterValue,
              })(<Checkbox></Checkbox>)}
            </FormItem>
          </Panel>
        </Collapse>
      </Form>
    </ModalBlur>
  )
}

modal.propTypes = {
  form: PropTypes.object.isRequired,
  volumeOptions: PropTypes.array,
  snapshotsOptions: PropTypes.array,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  item: PropTypes.object,
  onOk: PropTypes.func,
  getSnapshot: PropTypes.func,
  nodeTags: PropTypes.array,
  diskTags: PropTypes.array,
  defaultDataLocalityOption: PropTypes.array,
  defaultSnapshotDataIntegrityOption: PropTypes.array,
  tagsLoading: PropTypes.bool,
  backupTargets: PropTypes.array,
  defaultDataLocalityValue: PropTypes.string,
  defaultRevisionCounterValue: PropTypes.bool,
  v1DataEngineEnabled: PropTypes.bool,
  v2DataEngineEnabled: PropTypes.bool,
  backingImageOptions: PropTypes.array,
}

export default Form.create()(modal)
